<template>
  <div>
    <dx-grid
      :url="url"
      :columns="columns"
      :title="title"
      ref="grid"
      :actions="actions"
      :selectionEnabled="true"
    ></dx-grid>

    <v-dialog v-model="resolveConfilctsDialog" max-width="1300">
      <v-card>
        <v-card-title class="headline pb-0">
          Resolve Conflicts
          <span
            v-if="selected && selected.length"
            class="ml-2"
            style="font-size:0.85em; color:#888; font-weight:400;"
          >
            ({{ selected.length }} selected)
          </span>
        </v-card-title>
        <v-card-text style="overflow-y: auto; height: 450px;">
          <div v-if="errorMessages && errorMessages.length" class="mb-3">
            <v-alert type="error" value="true" dense outlined>
              <div v-for="(msg, idx) in errorMessages" :key="idx">
                {{ msg }}
              </div>
            </v-alert>
          </div>
          <v-data-table
            :headers="selectedHeaders"
            :items="selected"
            hide-actions
            class="elevation-1 mb-2"
            dense
          >
            <template v-slot:items="props">
              <tr
                v-bind:class="{
                  'resolve-conflicts-table-row': !expandedRows[
                    props.item.masterContactID
                  ],
                  'resolve-conflicts-table-row-expanded':
                    expandedRows[props.item.masterContactID],
                }"
              >
                <td>
                  <v-btn
                    class="py-0 my-0"
                    icon
                    small
                    @click="toggleExpand(props.item.masterContactID)"
                  >
                    <v-icon class="py-0">
                      {{
                        expandedRows[props.item.masterContactID]
                          ? "mdi-chevron-down"
                          : "mdi-chevron-right"
                      }}
                    </v-icon>
                  </v-btn>
                </td>
                <td>
                  <strong>
                    {{ props.item.hebrewFirstName }}
                    {{ props.item.hebrewLastName }}
                  </strong>
                  <div>
                    {{ props.item.firstName }} {{ props.item.lastName }}
                  </div>
                </td>
                <td>{{ props.item.masterContactID }}</td>
                <td>
                  {{ props.item.addressLine1 }}
                  <span v-if="props.item.apt"> Apt {{ props.item.apt }}</span>
                </td>
                <td>{{ props.item.city }}</td>
                <td>{{ props.item.state }}</td>
                <td>{{ props.item.zip }}</td>
                <td>{{ props.item.phoneNumber }}</td>
                <td>{{ props.item.status }}</td>
                <td>{{ props.item.conflictTypes }}</td>
                <td>{{ props.item.conflictFields }}</td>
              </tr>
              <tr
                v-if="expandedRows[props.item.masterContactID]"
                style="border-bottom: #43a047 1px solid !important;"
              >
                <td colspan="10" class="pa-0">
                  <div class="conflict-list">
                    <div
                      v-for="(conflict, conflictIndex) in conflictsByMaster(
                        props.item.masterContactID
                      )"
                      class="conflict-row-detail"
                    >
                      <div v-if="props.item.conflictTypes == 'CHANGE'">
                        <span class="conflict-field" v-if="conflict.field"
                          ><strong>{{ conflict.field }}:</strong></span
                        >
                        <span class="conflict-value"
                          ><span style="font-weight: 600;" class="mr-2"
                            >Genvite:</span
                          >{{ conflict.genvite }}</span
                        >
                        <span
                          v-if="
                            conflict.uta !== undefined && conflict.uta !== ''
                          "
                          class="conflict-value-alt"
                        >
                          →
                          <span style="font-weight: 600;" class="ml-2"
                            >Uta:</span
                          >
                          {{ conflict.uta }}
                        </span>
                      </div>
                      <div v-if="props.item.conflictTypes == 'ADDITION'">
                        <div>Conflict Type: Addition</div>
                      </div>
                      <div v-if="props.item.conflictTypes == 'DELETION'">
                        <div>Conflict Type: Deletion</div>
                      </div>
                      <v-select
                        v-model="conflict.action"
                        :items="getActionOptions(conflict.conflictType)"
                        item-text="label"
                        item-value="value"
                        dense
                        outlined
                        hide-details
                        :disabled="conflict.savedAction && !conflict.isEditing"
                        class="width-350 ml-2 mb-2 pt-2"
                      ></v-select>
                      <v-btn
                        v-if="conflict.savedAction && !conflict.isEditing"
                        icon
                        small
                        color="success"
                        class="ml-2"
                        @click="enableEdit(conflict)"
                      >
                        <v-icon small>mdi-pencil</v-icon>
                      </v-btn>
                      <v-autocomplete
                        v-if="conflict.action == 'new-account-link'"
                        class="width-200 ml-2 mb-2 pt-3"
                        hide-details
                        label="UTA Account ID"
                        placeholder="Start typing..."
                        hide-no-data
                        ref="newAccountID"
                        v-model="conflict.newAccountID"
                        :items="accounts"
                        :disabled="conflict.savedAction && !conflict.isEditing"
                      ></v-autocomplete>
                      <div v-if="showUtaIdError(conflict)" class="text-danger">
                        UTA Account ID is Required
                      </div>
                    </div>
                  </div>
                  <!-- Side-by-side comparison -->
                  <div class="comparison-container pa-4 mb-3">
                    <div class="comparison-header mb-2">
                      <strong>Genvite/UTA Comparison</strong>
                    </div>
                    <div class="comparison-content">
                      <div class="comparison-side">
                        <div class="comparison-side-header">
                          Genvite
                        </div>
                        <div class="comparison-details">
                          <div class="comparison-row">
                            <span class="comparison-label"
                              >masterContactID:</span
                            >
                            <span class="comparison-value">{{
                              props.item.masterContactID
                            }}</span>
                          </div>
                          <div class="comparison-row">
                            <span class="comparison-label">firstName:</span>
                            <span class="comparison-value">{{
                              props.item.firstName
                            }}</span>
                          </div>
                          <div class="comparison-row">
                            <span class="comparison-label">lastName:</span>
                            <span class="comparison-value">{{
                              props.item.lastName
                            }}</span>
                          </div>
                          <div class="comparison-row">
                            <span class="comparison-label"
                              >hebrewFirstName:</span
                            >
                            <span class="comparison-value">{{
                              props.item.hebrewFirstName
                            }}</span>
                          </div>
                          <div class="comparison-row">
                            <span class="comparison-label"
                              >hebrewLastName:</span
                            >
                            <span class="comparison-value">{{
                              props.item.hebrewLastName
                            }}</span>
                          </div>
                          <div class="comparison-row">
                            <span class="comparison-label">title:</span>
                            <span class="comparison-value">{{
                              props.item.title
                            }}</span>
                          </div>
                          <div class="comparison-row">
                            <span class="comparison-label">hebrewTitle:</span>
                            <span class="comparison-value">{{
                              props.item.hebrewTitle
                            }}</span>
                          </div>
                          <div class="comparison-row">
                            <span class="comparison-label"
                              >hebrewFirstNameAddditions:</span
                            >
                            <span class="comparison-value">{{
                              props.item.hebrewFirstNameAddditions
                            }}</span>
                          </div>
                          <div class="comparison-row">
                            <span class="comparison-label">hebrewSuffix:</span>
                            <span class="comparison-value">{{
                              props.item.hebrewSuffix
                            }}</span>
                          </div>
                          <div class="comparison-row">
                            <span class="comparison-label">addressLine1:</span>
                            <span class="comparison-value">{{
                              props.item.addressLine1
                            }}</span>
                          </div>
                          <div class="comparison-row">
                            <span class="comparison-label">addressLine2:</span>
                            <span class="comparison-value">{{
                              props.item.addressLine2
                            }}</span>
                          </div>
                          <div class="comparison-row">
                            <span class="comparison-label">apt:</span>
                            <span class="comparison-value">{{
                              props.item.apt
                            }}</span>
                          </div>
                          <div class="comparison-row">
                            <span class="comparison-label">city:</span>
                            <span class="comparison-value">{{
                              props.item.city
                            }}</span>
                          </div>
                          <div class="comparison-row">
                            <span class="comparison-label">state:</span>
                            <span class="comparison-value">{{
                              props.item.state
                            }}</span>
                          </div>
                          <div class="comparison-row">
                            <span class="comparison-label">zip:</span>
                            <span class="comparison-value">{{
                              props.item.zip
                            }}</span>
                          </div>
                          <div class="comparison-row">
                            <span class="comparison-label">country:</span>
                            <span class="comparison-value">{{
                              props.item.country
                            }}</span>
                          </div>
                          <div class="comparison-row">
                            <span class="comparison-label">phoneNumber:</span>
                            <span class="comparison-value">{{
                              props.item.phoneNumber
                            }}</span>
                          </div>
                          <div class="comparison-row">
                            <span class="comparison-label">status:</span>
                            <span class="comparison-value">{{
                              props.item.status
                            }}</span>
                          </div>
                          <div class="comparison-row">
                            <span class="comparison-label">utaID:</span>
                            <span class="comparison-value">{{
                              props.item.utaID
                            }}</span>
                          </div>
                          <div class="comparison-row">
                            <span class="comparison-label"
                              >fatherMasterContactID:</span
                            >
                            <span class="comparison-value">{{
                              props.item.fatherMasterContactID
                            }}</span>
                          </div>
                          <div class="comparison-row">
                            <span class="comparison-label"
                              >fatherInLawMasterContactID:</span
                            >
                            <span class="comparison-value">{{
                              props.item.fatherInLawMasterContactID
                            }}</span>
                          </div>
                          <div class="comparison-row">
                            <span class="comparison-label">fatherName:</span>
                            <span class="comparison-value">{{
                              props.item.fatherName
                            }}</span>
                          </div>
                          <div class="comparison-row">
                            <span class="comparison-label"
                              >fatherInLawName:</span
                            >
                            <span class="comparison-value">{{
                              props.item.fatherInLawName
                            }}</span>
                          </div>
                          <div class="comparison-row">
                            <span class="comparison-label">conflictTypes:</span>
                            <span class="comparison-value">{{
                              props.item.conflictTypes
                            }}</span>
                          </div>
                          <div class="comparison-row">
                            <span class="comparison-label"
                              >conflictFields:</span
                            >
                            <span class="comparison-value">{{
                              props.item.conflictFields
                            }}</span>
                          </div>
                        </div>
                      </div>
                      <div class="comparison-side">
                        <div class="comparison-side-header">
                          UTA
                        </div>
                        <template v-if="getAssociatedPerson(props.item.utaID)">
                          <div class="comparison-details">
                            <div class="comparison-row">
                              <span class="comparison-label">personID:</span>
                              <span class="comparison-value">{{
                                getAssociatedPerson(props.item.utaID).personID
                              }}</span>
                            </div>
                            <div class="comparison-row">
                              <span class="comparison-label"
                                >firstEnglish:</span
                              >
                              <span class="comparison-value">{{
                                getAssociatedPerson(props.item.utaID)
                                  .firstEnglish
                              }}</span>
                            </div>
                            <div class="comparison-row">
                              <span class="comparison-label">lastEnglish:</span>
                              <span class="comparison-value">{{
                                getAssociatedPerson(props.item.utaID)
                                  .lastEnglish
                              }}</span>
                            </div>
                            <div class="comparison-row">
                              <span class="comparison-label">firstHebrew:</span>
                              <span class="comparison-value">{{
                                getAssociatedPerson(props.item.utaID)
                                  .firstHebrew
                              }}</span>
                            </div>
                            <div class="comparison-row">
                              <span class="comparison-label">lastHebrew:</span>
                              <span class="comparison-value">{{
                                getAssociatedPerson(props.item.utaID).lastHebrew
                              }}</span>
                            </div>
                            <div class="comparison-row">
                              <span class="comparison-label"
                                >titleEnglish:</span
                              >
                              <span class="comparison-value">{{
                                getAssociatedPerson(props.item.utaID)
                                  .titleEnglish
                              }}</span>
                            </div>
                            <div class="comparison-row">
                              <span class="comparison-label">titleHebrew:</span>
                              <span class="comparison-value">{{
                                getAssociatedPerson(props.item.utaID)
                                  .titleHebrew
                              }}</span>
                            </div>
                            <div class="comparison-row">
                              <span class="comparison-label"
                                >suffixHebrew:</span
                              >
                              <span class="comparison-value">{{
                                getAssociatedPerson(props.item.utaID)
                                  .suffixHebrew
                              }}</span>
                            </div>
                            <div class="comparison-row">
                              <span class="comparison-label">inactive:</span>
                              <span class="comparison-value">{{
                                getAssociatedPerson(props.item.utaID).inactive
                              }}</span>
                            </div>
                            <div class="comparison-row">
                              <span class="comparison-label">lastUpdated:</span>
                              <span class="comparison-value">{{
                                getAssociatedPerson(props.item.utaID)
                                  .lastUpdated
                              }}</span>
                            </div>
                            <div class="comparison-row">
                              <span class="comparison-label">source:</span>
                              <span class="comparison-value">{{
                                getAssociatedPerson(props.item.utaID).source
                              }}</span>
                            </div>
                            <div class="comparison-row">
                              <span class="comparison-label"
                                >maternalGrandParentId:</span
                              >
                              <span class="comparison-value">{{
                                getAssociatedPerson(props.item.utaID)
                                  .maternalGrandParentId
                              }}</span>
                            </div>
                            <div class="comparison-row">
                              <span class="comparison-label"
                                >maternalGrandParentId2:</span
                              >
                              <span class="comparison-value">{{
                                getAssociatedPerson(props.item.utaID)
                                  .maternalGrandParentId2
                              }}</span>
                            </div>
                            <div class="comparison-row">
                              <span class="comparison-label">parentId:</span>
                              <span class="comparison-value">{{
                                getAssociatedPerson(props.item.utaID).parentId
                              }}</span>
                            </div>
                            <div class="comparison-row">
                              <span class="comparison-label"
                                >paternalGrandParentId:</span
                              >
                              <span class="comparison-value">{{
                                getAssociatedPerson(props.item.utaID)
                                  .paternalGrandParentId
                              }}</span>
                            </div>
                            <div class="comparison-row">
                              <span class="comparison-label"
                                >paternalGrandParentId2:</span
                              >
                              <span class="comparison-value">{{
                                getAssociatedPerson(props.item.utaID)
                                  .paternalGrandParentId2
                              }}</span>
                            </div>
                            <div class="comparison-row">
                              <span class="comparison-label">weddingYear:</span>
                              <span class="comparison-value">{{
                                getAssociatedPerson(props.item.utaID)
                                  .weddingYear
                              }}</span>
                            </div>
                            <!-- Phones Section -->
                            <template
                              v-if="
                                getAssociatedPerson(props.item.utaID).phones &&
                                  getAssociatedPerson(props.item.utaID).phones
                                    .length > 0
                              "
                            >
                              <div
                                v-for="(phone,
                                phoneIndex) in getAssociatedPerson(
                                  props.item.utaID
                                ).phones"
                                class="comparison-phone-section"
                              >
                                <div class="comparison-section-header">
                                  Phone {{ phoneIndex + 1 }}
                                </div>
                                <div class="comparison-row">
                                  <span class="comparison-label">phoneID:</span>
                                  <span class="comparison-value">{{
                                    phone.phoneID
                                  }}</span>
                                </div>
                                <div class="comparison-row">
                                  <span class="comparison-label"
                                    >phoneNum:</span
                                  >
                                  <span class="comparison-value">{{
                                    phone.phoneNum
                                  }}</span>
                                </div>
                                <div class="comparison-row">
                                  <span class="comparison-label"
                                    >phoneNumCleaned:</span
                                  >
                                  <span class="comparison-value">{{
                                    phone.phoneNumCleaned
                                  }}</span>
                                </div>
                                <div class="comparison-row">
                                  <span class="comparison-label"
                                    >contactType:</span
                                  >
                                  <span class="comparison-value">{{
                                    phone.contactType
                                  }}</span>
                                </div>
                                <div class="comparison-row">
                                  <span class="comparison-label"
                                    >personID:</span
                                  >
                                  <span class="comparison-value">{{
                                    phone.personID
                                  }}</span>
                                </div>
                                <div class="comparison-row">
                                  <span class="comparison-label"
                                    >lastUpdated:</span
                                  >
                                  <span class="comparison-value">{{
                                    phone.lastUpdated
                                  }}</span>
                                </div>
                                <div class="comparison-row">
                                  <span class="comparison-label">source:</span>
                                  <span class="comparison-value">{{
                                    phone.source
                                  }}</span>
                                </div>
                              </div>
                            </template>
                            <!-- Addresses Section -->
                            <template
                              v-if="
                                getAssociatedPerson(props.item.utaID)
                                  .addresses &&
                                  getAssociatedPerson(props.item.utaID)
                                    .addresses.length > 0
                              "
                            >
                              <div
                                v-for="(address,
                                addressIndex) in getAssociatedPerson(
                                  props.item.utaID
                                ).addresses"
                                class="comparison-address-section"
                              >
                                <div class="comparison-section-header">
                                  Address {{ addressIndex + 1 }}
                                </div>
                                <div class="comparison-row">
                                  <span class="comparison-label"
                                    >addressID:</span
                                  >
                                  <span class="comparison-value">{{
                                    address.addressID
                                  }}</span>
                                </div>
                                <div class="comparison-row">
                                  <span class="comparison-label"
                                    >address1:</span
                                  >
                                  <span class="comparison-value">{{
                                    address.address1
                                  }}</span>
                                </div>
                                <div class="comparison-row">
                                  <span class="comparison-label"
                                    >address2:</span
                                  >
                                  <span class="comparison-value">{{
                                    address.address2
                                  }}</span>
                                </div>
                                <div class="comparison-row">
                                  <span class="comparison-label"
                                    >address3:</span
                                  >
                                  <span class="comparison-value">{{
                                    address.address3
                                  }}</span>
                                </div>
                                <div class="comparison-row">
                                  <span class="comparison-label"
                                    >addressType:</span
                                  >
                                  <span class="comparison-value">{{
                                    address.addressType
                                  }}</span>
                                </div>
                                <div class="comparison-row">
                                  <span class="comparison-label">apt:</span>
                                  <span class="comparison-value">{{
                                    address.apt
                                  }}</span>
                                </div>
                                <div class="comparison-row">
                                  <span class="comparison-label">house:</span>
                                  <span class="comparison-value">{{
                                    address.house
                                  }}</span>
                                </div>
                                <div class="comparison-row">
                                  <span class="comparison-label">street:</span>
                                  <span class="comparison-value">{{
                                    address.street
                                  }}</span>
                                </div>
                                <div class="comparison-row">
                                  <span class="comparison-label">city:</span>
                                  <span class="comparison-value">{{
                                    address.city
                                  }}</span>
                                </div>
                                <div class="comparison-row">
                                  <span class="comparison-label">state:</span>
                                  <span class="comparison-value">{{
                                    address.state
                                  }}</span>
                                </div>
                                <div class="comparison-row">
                                  <span class="comparison-label">zip:</span>
                                  <span class="comparison-value">{{
                                    address.zip
                                  }}</span>
                                </div>
                                <div class="comparison-row">
                                  <span class="comparison-label">country:</span>
                                  <span class="comparison-value">{{
                                    address.country
                                  }}</span>
                                </div>
                                <div class="comparison-row">
                                  <span class="comparison-label"
                                    >personID:</span
                                  >
                                  <span class="comparison-value">{{
                                    address.personID
                                  }}</span>
                                </div>
                                <div class="comparison-row">
                                  <span class="comparison-label"
                                    >lastUpdated:</span
                                  >
                                  <span class="comparison-value">{{
                                    address.lastUpdated
                                  }}</span>
                                </div>
                                <div class="comparison-row">
                                  <span class="comparison-label">source:</span>
                                  <span class="comparison-value">{{
                                    address.source
                                  }}</span>
                                </div>
                                <div class="comparison-row">
                                  <span class="comparison-label"
                                    >sourceID:</span
                                  >
                                  <span class="comparison-value">{{
                                    address.sourceID
                                  }}</span>
                                </div>
                              </div>
                            </template>
                          </div>
                        </template>
                        <div class="comparison-details" v-else>
                          <div class="comparison-row">
                            <span
                              class="comparison-value"
                              style="color: #999; font-style: italic;"
                              >No associated person found</span
                            >
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions class="pb-3 pt-3">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="resolveConfilctsDialog = false"
            >Cancel</v-btn
          >
          <v-btn color="success" :loading="loading" @click="saveConflictActions"
            >Save</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  data() {
    return {
      errorMessages: [],
      title: "Genvite Conflicts",
      url: "genviteConflicts",
      actions: [],
      resolveConfilctsDialog: false,
      conflicts: [],
      persons: [],
      selected: [],
      expandedRows: {},
      loading: false,
      accounts: [],

      actionOptions: [
        {
          value: "new-account-create",
          label: "New Account: Create New account",
        },
        {
          value: "new-account-link",
          label: "New Account: Link with existing account",
        },
        { value: "conflict-update", label: "Conflict: Update From Genvite" },
        { value: "conflict-ignore", label: "Conflict: Ignore" },
      ],

      columns: [
        {
          label: "Master Contact ID",
          field: "masterContactID",
          visible: true,
          filterValues: [],
          filterValue: null,
          selectedFilterOperation: null,
        },
        {
          label: "First Name",
          field: "firstName",
          visible: true,
          filterValues: [],
          filterValue: null,
          selectedFilterOperation: null,
        },
        {
          label: "Last Name",
          field: "lastName",
          visible: true,
          filterValues: [],
          filterValue: null,
          selectedFilterOperation: null,
        },
        {
          label: "Hebrew First Name",
          field: "hebrewFirstName",
          visible: true,
          filterValues: [],
          filterValue: null,
          selectedFilterOperation: null,
        },
        {
          label: "Hebrew Last Name",
          field: "hebrewLastName",
          visible: true,
          filterValues: [],
          filterValue: null,
          selectedFilterOperation: null,
        },
        {
          label: "Address Line 1",
          field: "addressLine1",
          visible: true,
          filterValues: [],
          filterValue: null,
          selectedFilterOperation: null,
        },
        {
          label: "Apt",
          field: "apt",
          visible: true,
          filterValues: [],
          filterValue: null,
          selectedFilterOperation: null,
        },
        {
          label: "Status",
          field: "status",
          visible: true,
          filterValues: [],
          filterValue: null,
          selectedFilterOperation: null,
        },
        {
          label: "Conflict Types",
          field: "conflictTypes",
          visible: true,
          filterValues: [],
          filterValue: null,
          selectedFilterOperation: null,
        },
        {
          label: "Conflict Fields",
          field: "conflictFields",
          visible: true,
          filterValues: [],
          filterValue: null,
          selectedFilterOperation: null,
        },
        {
          label: "Conflict Actions",
          field: "conflictActions",
          visible: true,
          filterValues: [],
          filterValue: null,
          selectedFilterOperation: null,
        },
        {
          label: "Uta ID",
          field: "utaID",
          visible: true,
          filterValues: [],
          filterValue: null,
          selectedFilterOperation: null,
        },
        {
          label: "Address Line 2",
          field: "addressLine2",
          visible: true,
          filterValues: [],
          filterValue: null,
          selectedFilterOperation: null,
        },
        {
          label: "City",
          field: "city",
          visible: true,
          filterValues: [],
          filterValue: null,
          selectedFilterOperation: null,
        },
        {
          label: "State",
          field: "state",
          visible: true,
          filterValues: [],
          filterValue: null,
          selectedFilterOperation: null,
        },
        {
          label: "Zip",
          field: "zip",
          visible: true,
          filterValues: [],
          filterValue: null,
          selectedFilterOperation: null,
        },
        {
          label: "Country",
          field: "country",
          visible: true,
          filterValues: [],
          filterValue: null,
          selectedFilterOperation: null,
        },
        {
          label: "Phone Number",
          field: "phoneNumber",
          visible: true,
          filterValues: [],
          filterValue: null,
          selectedFilterOperation: null,
        },
        {
          label: "Title",
          field: "title",
          visible: true,
          filterValues: [],
          filterValue: null,
          selectedFilterOperation: null,
        },
        {
          label: "Hebrew Title",
          field: "hebrewTitle",
          visible: true,
          filterValues: [],
          filterValue: null,
          selectedFilterOperation: null,
        },
        {
          label: "Hebrew First Name Additions",
          field: "hebrewFirstNameAddditions",
          visible: true,
          filterValues: [],
          filterValue: null,
          selectedFilterOperation: null,
        },
        {
          label: "Hebrew Suffix",
          field: "hebrewSuffix",
          visible: true,
          filterValues: [],
          filterValue: null,
          selectedFilterOperation: null,
        },
        {
          label: "Father Master Contact ID",
          field: "fatherMasterContactID",
          visible: true,
          filterValues: [],
          filterValue: null,
          selectedFilterOperation: null,
        },
        {
          label: "Father-in-law Master Contact ID",
          field: "fatherInLawMasterContactID",
          visible: true,
          filterValues: [],
          filterValue: null,
          selectedFilterOperation: null,
        },
        {
          label: "Father-in-law Name",
          field: "fatherInLawName",
          visible: true,
          filterValues: [],
          filterValue: null,
          selectedFilterOperation: null,
        },
        {
          label: "Father Name",
          field: "fatherName",
          visible: true,
          filterValues: [],
          filterValue: null,
          selectedFilterOperation: null,
        },
      ],
      selectedHeaders: [
        { text: "", value: "expand", sortable: false },
        { text: "Name", value: "name", sortable: false },
        { text: "MasterContactID", value: "masterContactID", sortable: false },
        { text: "Address", value: "addressLine1", sortable: false },
        { text: "City", value: "city", sortable: false },
        { text: "State", value: "state", sortable: false },
        { text: "Zip", value: "zip", sortable: false },
        { text: "Phone", value: "phoneNumber", sortable: false },
        { text: "Status", value: "status", sortable: false },
        { text: "Conflict Type", value: "conflictTypes", sortable: false },
        { text: "Conflict Fields", value: "conflictFields", sortable: false },
      ],
      conflictHeaders: [
        { text: "Field", value: "field" },
        { text: "Genvite", value: "genvite" },
        { text: "UTA", value: "uta" },
        { text: "Action", value: "action", sortable: false },
      ],
    };
  },
  watch: {},
  created() {
    this.actions.push({
      text: "Resolve Confilcts",
      action: this.openResolveConfilctsDialog,
      showOnSelection: true,
    });

    this.loadAccounts();
  },
  methods: {
    async loadAccounts() {
      const response = await this.$http.get(
        "api/genviteConflicts/getPersonIDs"
      );

      this.accounts = response;
    },
    async openResolveConfilctsDialog() {
      this.selected = this.$refs.grid.getSelectedRowsData();

      const response = await this.$http.post(
        "api/genviteConflicts/getForMasterIDs",
        this.selected.map((s) => s.masterContactID)
      );

      this.conflicts = response.conflicts.map((conflict) => {
        conflict.newAccountID = "";
        // Track if this conflict has a previously saved action
        conflict.savedAction = conflict.action ? conflict.action : null;
        conflict.isEditing = false;
        return conflict;
      });

      // Combine personPhones, emails, and addresses into persons based on personID
      this.persons = response.persons.map((person) => {
        const personID = person.personID;
        return {
          ...person,
          phones: (response.personPhones || []).filter(
            (phone) => phone.personID === personID
          ),
          emails: (response.emails || []).filter(
            (email) => email.personID === personID
          ),
          addresses: (response.addresses || []).filter(
            (address) => address.personID === personID
          ),
        };
      });

      this.errorMessages = [];

      this.resolveConfilctsDialog = true;
    },
    toggleExpand(masterContactID) {
      this.$set(
        this.expandedRows,
        masterContactID,
        !this.expandedRows[masterContactID]
      );
    },
    enableEdit(conflict) {
      this.$set(conflict, "isEditing", true);
    },
    conflictsByMaster(masterContactID) {
      return this.conflicts.filter(
        (c) => c.masterContactID === masterContactID
      );
    },
    getAssociatedPerson(utaID) {
      if (!utaID) return null;
      return this.persons.find((p) => p.personID == utaID);
    },
    showUtaIdError(conflict) {
      // Show error if validation failed for this conflict and errorMessages are present
      if (!this.errorMessages.length) return false;
      return (
        !conflict.newAccountID &&
        this.errorMessages.some(
          (msg) =>
            msg.includes(`MasterContactID ${conflict.masterContactID}`) &&
            msg.includes("UTA Account ID")
        )
      );
    },
    getActionOptions(type) {
      if (["CHANGE", "DELETION"].includes(type)) {
        return [
          { value: "conflict-update", label: "Conflict: Update From Genvite" },
          { value: "conflict-ignore", label: "Conflict: Ignore" },
        ];
      } else if (type === "ADDITION") {
        return [
          {
            value: "new-account-create",
            label: "New Account: Create New account",
          },
          {
            value: "new-account-link",
            label: "New Account: Link with existing account",
          },
        ];
      }
      return [];
    },
    saveConflictActions() {
      this.loading = true;
      this.errorMessages = [];
      // Validation
      for (const conflict of this.conflicts) {
        if (!conflict.action) {
          this.errorMessages.push(
            `Missing action for MasterContactID ${conflict.masterContactID}${
              conflict.field ? ", field " + conflict.field : ""
            }`
          );
        }
        if (conflict.action === "new-account-link" && !conflict.newAccountID) {
          this.errorMessages.push(
            `Missing UTA Account ID for link (MasterContactID ${
              conflict.masterContactID
            }${conflict.field ? ", field " + conflict.field : ""})`
          );
        }
      }
      if (this.errorMessages.length) {
        this.loading = false;
        // Errors are shown in dialog, do not notify
        return;
      }
      // Prepare links array
      const links = this.conflicts
        .filter((c) => c.action === "new-account-link" && c.newAccountID)
        .map((c) => ({
          AnslID: c.masterContactID,
          UtaID: c.newAccountID,
          ByUser: this.$store.state.authentication.user || "",
        }));
      // Prepare payload
      const payload = {
        user: this.$store.state.authentication.user || "",
        conflicts: this.conflicts,
        links,
      };
      this.$http
        .post("api/genviteConflicts/UpdateConflictsAndLinks", payload)
        .then((response) => {
          // Handle error returned as a normal response
          if (response && response.error) {
            this.errorMessages = [response.error];
            if (Array.isArray(response.details) && response.details.length) {
              this.errorMessages = this.errorMessages.concat(response.details);
            }
            this.loading = false;
            return;
          }
          this.$notify({
            type: "success",
            group: "notification",
            text: "Conflicts saved successfully",
          });
          this.resolveConfilctsDialog = false;
          this.loading = false;
          this.errorMessages = [];
          // Optionally refresh grid
          if (this.$refs.grid && this.$refs.grid.reloadGrid) {
            this.$refs.grid.reloadGrid();
          }
        })
        .catch((err) => {
          this.loading = false;
          // Handle API error response with details
          if (err && err.error) {
            this.errorMessages = [err.error];
            if (Array.isArray(err.details) && err.details.length) {
              this.errorMessages = this.errorMessages.concat(err.details);
            }
          } else {
            this.errorMessages = ["Failed to save conflicts"];
          }
        });
    },
  },
};
</script>

<style>
.gray-fg {
  color: #999;
}
.conflict-row {
  margin-bottom: 8px;
  border-bottom: 1px solid #eee;
}
.row-title {
  font-weight: bold;
  margin-left: 8px;
  font-size: 1.1em;
}
.conflict-list {
  margin: 0 0 0 32px;
  padding: 0;
}
.conflict-row-detail {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 4px;
  font-size: 0.97em;
}
.conflict-field {
  /* min-width: 90px; */
  font-weight: 500;
  padding-right: 8px;
}
.conflict-value {
  color: #333;
}
.conflict-value-alt {
  color: #888;
  padding-left: 8px;
}
.v-data-table {
  background: #fafafa;
  border-radius: 6px;
}
.v-card-title.headline {
  font-size: 1.3em;
  font-weight: 500;
}
.v-card-actions {
  margin-top: 8px;
}
.row-details {
  border-radius: 6px;
  background: #f8f9fa;
  margin-bottom: 4px;
  padding: 4px 8px;
}
.row-details-compact {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 18px;
  font-size: 0.93em;
  align-items: center;
  line-height: 1.5;
}
.row-details-compact span {
  white-space: nowrap;
}
.resolve-conflicts-table-row {
  border-bottom: #43a047 1px solid !important;
  border-top: #43a047 1px solid !important;
}
.resolve-conflicts-table-row-expanded {
  border-bottom: #43a047 1px solid !important;
  border-top: #43a047 1px solid !important;
}
.comparison-container {
  background: #f5f5f5;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
}
.comparison-header {
  font-size: 1.05em;
  color: #333;
  border-bottom: 1px solid #ddd;
  padding-bottom: 8px;
}
.comparison-content {
  display: flex;
  gap: 20px;
}
.comparison-side {
  flex: 1;
  background: white;
  border-radius: 4px;
  padding: 12px;
  border: 1px solid #e0e0e0;
}
.comparison-side-header {
  font-weight: 600;
  font-size: 0.95em;
  color: #43a047;
  margin-bottom: 10px;
  padding-bottom: 6px;
  border-bottom: 1px solid #e0e0e0;
}
.comparison-details {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.comparison-row {
  display: flex;
  flex-wrap: wrap;
  font-size: 0.9em;
  line-height: 1.4;
}
.comparison-label {
  font-weight: 500;
  color: #666;
  min-width: 120px;
  margin-right: 8px;
}
.comparison-value {
  color: #333;
  flex: 1;
}
.comparison-phone-section,
.comparison-address-section {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #e0e0e0;
}
.comparison-section-header {
  font-weight: 600;
  font-size: 0.9em;
  color: #666;
  margin-bottom: 8px;
  padding-bottom: 4px;
  border-bottom: 1px dashed #ddd;
}
</style>
